import {
  currentTz,
  DATA_FORMAT_VERSION,
  newId,
  OpLogKind,
  RecordType,
  SettingKey,
  settingId,
  type HlcClock,
} from '@wiztime/core';
import { WriteSession } from './mutations/session';
import { MetaKey, type Repo } from './repo';

/**
 * Data-format migration of the stored data (data dictionary §10). Steps run in order from the
 * stored version to this build's DATA_FORMAT_VERSION before any write; each runs in one
 * transaction together with the new version number, and must give the same result when run
 * again. Before the first step that rewrites data, every record is copied to the backup store.
 */

export interface MigrationStep {
  /** The data-format version the step produces. */
  readonly version: number;
  /** Whether the step rewrites records (a backup is taken before the first such step). */
  readonly rewrites: boolean;
  /** Raises the minimum reader version to this step's version: older builds then open read-only. */
  readonly breaksOlderBuilds: boolean;
  readonly description: string;
  run(session: WriteSession): Promise<{ readonly written: number }>;
}

/** The steps of this build, ascending by version. */
export const MIGRATION_STEPS: readonly MigrationStep[] = [
  {
    version: 2,
    rewrites: true,
    breaksOlderBuilds: false,
    // Check-in occurrences (data dictionary §3.2): legacy check-in data is interpreted on read and
    // rewritten only when touched, under the day start frozen here; the operation-log table
    // comes with the database version.
    description: 'check-in occurrences: freeze the legacy day start',
    run: async (session) => {
      const id = settingId(SettingKey.CheckinLegacyDayStartMin);
      if ((await session.find(id))?.data != null) return { written: 0 };
      const { dayStartMin } = await session.settings();
      await session.write(RecordType.Setting, id, { key: SettingKey.CheckinLegacyDayStartMin, value: dayStartMin });
      return { written: 1 };
    },
  },
];

export const MigrationStatus = {
  /** The data is at this build's format (after running the steps, if any). */
  Ready: 'ready',
  /** Written by a newer build that older builds may not write: open read-only. */
  TooNew: 'tooNew',
  /** A step failed: the data stays at the last completed version; open read-only. */
  Failed: 'failed',
} as const;
export type MigrationStatus = (typeof MigrationStatus)[keyof typeof MigrationStatus];

export interface MigrationOutcome {
  readonly status: MigrationStatus;
  /** Data-format version of the stored data after the run. */
  readonly version: number;
  /** Versions of the steps that ran. */
  readonly ran: readonly number[];
  readonly error?: string;
}

const readVersion = (value: string | undefined): number => {
  const parsed = value === undefined ? NaN : Number(value);
  return Number.isInteger(parsed) && parsed >= 1 ? parsed : 1;
};

export interface MigrateOptions {
  readonly repo: Repo;
  readonly clock: HlcClock;
  readonly nowMs: () => number;
  readonly deviceId: string;
  readonly appVersion: string;
  readonly steps?: readonly MigrationStep[];
  readonly target?: number;
}

/** Bring the stored data to this build's data format (data dictionary §10). */
export async function migrateData(options: MigrateOptions): Promise<MigrationOutcome> {
  const { repo, clock, nowMs, deviceId, appVersion } = options;
  const steps = options.steps ?? MIGRATION_STEPS;
  const target = options.target ?? DATA_FORMAT_VERSION;
  const minReader = readVersion(await repo.getMeta(MetaKey.MinReaderFormat));
  let version = readVersion(await repo.getMeta(MetaKey.DataFormatVersion));
  if (minReader > target) return { status: MigrationStatus.TooNew, version, ran: [] };
  const pending = steps.filter((step) => step.version > version && step.version <= target);
  const ran: number[] = [];
  let backedUp = false;
  for (const step of pending) {
    try {
      if (step.rewrites && !backedUp) {
        await repo.backup();
        backedUp = true;
      }
      await repo.transaction(async (tx) => {
        // Another tab may have completed the step meanwhile.
        if (readVersion(await tx.getMeta(MetaKey.DataFormatVersion)) >= step.version) return;
        const at = nowMs();
        const session = new WriteSession(tx, { clock, deviceId, nowMs: at, tz: currentTz(), newId: () => newId(at) });
        const { written } = await step.run(session);
        await tx.setMeta(MetaKey.DataFormatVersion, String(step.version));
        if (step.breaksOlderBuilds) await tx.setMeta(MetaKey.MinReaderFormat, String(step.version));
        await tx.appendOpLog({
          at: new Date(nowMs()).toISOString(),
          deviceId,
          appVersion,
          kind: OpLogKind.Migration,
          name: `dataFormat ${version} → ${step.version}`,
          summary: { description: step.description, written, backup: backedUp },
          changes: [],
        });
      });
      version = step.version;
      ran.push(step.version);
    } catch (error) {
      return {
        status: MigrationStatus.Failed,
        version,
        ran,
        error: error instanceof Error ? error.message : String(error),
      };
    }
  }
  // No step for the remaining versions (a database with nothing to convert): record the target.
  if (version < target) {
    await repo.transaction((tx) => tx.setMeta(MetaKey.DataFormatVersion, String(target)));
    version = target;
  }
  return { status: MigrationStatus.Ready, version, ran };
}
